import './OnlinePurchase2ConsultModal.css';

import React, { useMemo, useState } from 'react';
import { Button } from '@vtblife/uikit';

import { useUserDetailsLoader } from '@search/auth/src/userDetails/userDetails';
import classname from '@search/classname/src';
import { formatPhone } from '@search/helpers/src/formatPhone';

import { useClassifiedOfferDataLayerPush } from '../../../../../domain/offer/analytics/ClassifiedOfferGtmContext';
import { ConsultModal, ConsultModalProps } from '../../ConsultModal/ConsultModal';
import { usePromoOnlinePurchase2EmailMutation } from './usePromoOnlinePurchase2EmailMutation';

const cn = classname.bind(null, 'OnlinePurchase2ConsultModal');

const PHONE_NUMBER_LENGTH = 11;
const DEFAULT_REQUEST_ERROR_MESSAGE = 'Что-то пошло не так, попробуйте позже.';

interface IOnlinePurchase2ConsultModalProps {
    onClose?(): void;
    isMobile?: boolean;
    offerUrl?: string;
}

export const OnlinePurchase2ConsultModal: React.FC<IOnlinePurchase2ConsultModalProps> = ({
    isMobile,
    offerUrl,
    onClose,
}) => {
    const sendPromoOnlineEmail = usePromoOnlinePurchase2EmailMutation();

    const userInfoLoad = useUserDetailsLoader();
    const userInfo = userInfoLoad({});
    const person = userInfo.data?.userProfile.person;

    const [ name, setName ] = useState('');
    const [ phoneNumber, setPhoneNumber ] = useState('');
    const [ isPhoneTouched, setPhoneTouched ] = useState(false);
    const [ isSaving, setSaving ] = useState(false);
    const [ isSended, setIsSended ] = useState(false);

    const isPhoneValid = phoneNumber.length === PHONE_NUMBER_LENGTH;
    const domain: ConsultModalProps['domain'] = useMemo(() => ({
        setName,
        setPhone: setPhoneNumber,
        setPhoneTouched: () => setPhoneTouched(true),
        setSaving,
        isFormSending: isSaving,
        isFormSended: isSended,
        phone: phoneNumber,
        isPhoneTouched,
        name,
        phoneInputValid: isPhoneValid
    }), [ isSaving, isSended, phoneNumber, name, isPhoneTouched, isPhoneValid ]);

    React.useEffect(() => {
        if (person?.name) setName(person?.name ?? '');
        if (person?.primaryPhone?.number) setPhoneNumber(person?.primaryPhone?.number);
    }, [ person?.name, person?.primaryPhone ]);

    const dataLayerPush = useClassifiedOfferDataLayerPush();

    const sendCallback = React.useCallback((e?: React.FormEvent<HTMLFormElement> | React.KeyboardEvent<HTMLInputElement>) => {
        setPhoneTouched(true);
        // prevent submitted form updating page
        e?.preventDefault();
        if (! isPhoneValid) return;
        setSaving(true);

        dataLayerPush({
            event: 'up_action',
            theme: 'online_deal',
            type: 'consult'
        });

        sendPromoOnlineEmail({
            name,
            phone: phoneNumber,
            offerLink: offerUrl ?? window.location.href
        })
            .then(() => setIsSended(true))
            .finally(() => setSaving(false));
    }, [ isPhoneValid, name, phoneNumber, sendPromoOnlineEmail, dataLayerPush ]);

    const titleText = isSended ? 'Заявка принята' : 'Получить консультацию';
    const subtitleText = isSended ?
        <>Скоро мы перезвоним вам по номеру <span style={{ whiteSpace: 'nowrap' }}>{formatPhone(phoneNumber, { skipParenthesis: true })}</span></> :
        'Оставьте свой номер телефона, и\u00A0мы\u00A0обязательно перезвоним.';

    return (
        <ConsultModal
            title={titleText}
            subtitle={subtitleText}
            onClose={onClose}
            sizeFields='l'
            isMobile={isMobile}

            send={sendCallback}
            domain={domain}
            sendedContent={
                <div className={cn('sended-block')}>
                    <Button
                        fullWidth
                        variant='secondary'
                        onClick={onClose}
                    >Закрыть</Button>
                </div>}
        />
    );
};
